import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ExternalLink, Maximize, X as CloseIcon, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';

// Image imports
import img37 from '@/assets/img/37.jpg';
import img38 from '@/assets/img/38.jpg';
import img39 from '@/assets/img/39.jpg';
import img40 from '@/assets/img/40.jpg';
import img41 from '@/assets/img/41.jpg';
import img42 from '@/assets/img/42.jpg';
import img43 from '@/assets/img/43.jpg';
import img44 from '@/assets/img/44.jpg';
import img45 from '@/assets/img/45.jpg';
import img46 from '@/assets/img/46.jpg';
import img47 from '@/assets/img/47.jpg';
import img48 from '@/assets/img/48.jpg';
import img49 from '@/assets/img/49.jpg';
import img50 from '@/assets/img/50.jpg';
import img51 from '@/assets/img/51.jpg';
import img52 from '@/assets/img/52.jpg';
import img53 from '@/assets/img/53.jpg';
import img54 from '@/assets/img/54.jpg';
import img55 from '@/assets/img/55.jpg';

const artworks = [
  { id: 'dr037', src: img37, title: 'Urban Pulse', medium: 'Graphite on Bristol', dimensions: '24x36', year: 2023, alt: 'Detailed drawing of cityscape with strong geometric patterns' },
  { id: 'dr038', src: img38, title: 'Eternal Forms', medium: 'Charcoal on Paper', dimensions: '20x24', year: 2024, alt: 'Classical sculpture study in dramatic chiaroscuro' },
  { id: 'dr039', src: img39, title: 'Liquid Shadows', medium: 'Ink Wash', dimensions: '30x40', year: 2023, alt: 'Abstract drawing of water reflections and shadows' },
  { id: 'dr040', src: img40, title: 'The Architect', medium: 'Pencil & Conte', dimensions: '18x24', year: 2024, alt: 'Portrait sketch of a designer at work in studio' },
  { id: 'dr041', src: img41, title: 'Metropolis Sketch', medium: 'Fine Liner on Vellum', dimensions: '40x60', year: 2023, alt: 'Intricate line drawing of city aerial view' },
  { id: 'dr042', src: img42, title: 'Silent Dialogue', medium: 'Pastel on Toned Paper', dimensions: '16x20', year: 2022, alt: 'Figurative drawing of two figures in contemplative pose' },
  { id: 'dr043', src: img43, title: 'Industrial Symphony', medium: 'Charcoal & Chalk', dimensions: '24x36', year: 2024, alt: 'Expressive drawing of factory interiors' },
  { id: 'dr044', src: img44, title: 'Veiled Light', medium: 'Silverpoint on Prepared Ground', dimensions: '20x30', year: 2023, alt: 'Delicate drawing of soft diffused light through fabric' },
  { id: 'dr045', src: img45, title: 'The Observer', medium: 'Graphite Sketch', dimensions: '18x24', year: 2024, alt: 'Quick sketch capturing a lone figure on the street' },
  { id: 'dr046', src: img46, title: 'Minimal Wave Study', medium: 'Ink on Rice Paper', dimensions: '30x30', year: 2023, alt: 'Minimalist ink drawing of an ocean wave' },
  { id: 'dr047', src: img47, title: 'Concrete Dreams', medium: 'Carbon Pencil', dimensions: '20x24', year: 2024, alt: 'Drawing of Brutalist architecture with strong shadows' },
  { id: 'dr048', src: img48, title: 'Fleeting Moment', medium: 'Conté Crayon', dimensions: '24x36', year: 2023, alt: 'Sketch capturing blurred motion of urban commuters' },
  { id: 'dr049', src: img49, title: 'The Thinker Study', medium: 'Charcoal Sketch', dimensions: '16x20', year: 2024, alt: 'Life drawing study interpreting Rodin work' },
  { id: 'dr050', src: img50, title: 'Geometric Dawn Sketch', medium: 'Pencil on Paper', dimensions: '30x45', year: 2023, alt: 'Architectural sketch of early morning shadows' },
  { id: 'dr051', src: img51, title: 'Silent Streets Drawing', medium: 'Graphite Wash', dimensions: '20x24', year: 2024, alt: 'Atmospheric drawing of an empty urban landscape at dawn' },
  { id: 'dr052', src: img52, title: 'Elemental Sketch', medium: 'Chalk on Black Paper', dimensions: '24x36', year: 2023, alt: 'Expressive drawing of abstract natural forms' },
  { id: 'dr053', src: img53, title: 'The Dancer in Lines', medium: 'Ink & Brush', dimensions: '16x20', year: 2024, alt: 'Dynamic line drawing study of a ballet performer' },
  { id: 'dr054', src: img54, title: 'Urban Tapestry Sketch', medium: 'Pen and Ink', dimensions: '40x60', year: 2023, alt: 'Detailed panoramic sketch of a cityscape' },
  { id: 'dr055', src: img55, title: 'Timeless Lines', medium: 'Graphite Portrait', dimensions: '24x36', year: 2024, alt: 'Classic portrait drawing with a modern sensibility' }
];

const FilmstripItem = ({ artwork, index, isActive, onClick, onHover }) => {
  const shouldReduceMotion = useReducedMotion();
  return (
    <motion.div
      className={`bnw-filmstrip-item ${isActive ? 'active' : ''}`}
      onClick={() => onClick(artwork, index)}
      onMouseEnter={() => onHover(artwork)}
      onMouseLeave={() => onHover(null)}
      variants={{
        initial: { opacity: 0, x: -20 },
        animate: { opacity: 1, x: 0, transition: { duration: 0.3, delay: index * 0.05 } }
      }}
      whileHover={shouldReduceMotion ? {} : { scale: 1.05, transition: { type: 'spring', stiffness: 300 } }}
    >
      <motion.img 
        src={artwork.src} 
        alt={`Thumbnail of ${artwork.title}`} 
        className="bnw-filmstrip-thumbnail"
        layoutId={`bnw-thumbnail-${artwork.id}`}
      />
      <div className="bnw-filmstrip-item-info">
        <h4 className="bnw-filmstrip-item-title">{artwork.title}</h4>
        <p className="bnw-filmstrip-item-year">{artwork.year}</p>
      </div>
       {isActive && <motion.div className="bnw-filmstrip-active-indicator" layoutId="bnw-active-filmstrip-indicator" />}
    </motion.div>
  );
};

const Drawings = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoveredArtwork, setHoveredArtwork] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const filmstripRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();

  const currentArtwork = artworks[activeIndex];

  useEffect(() => {
    const prefersLightScheme = window.matchMedia('(prefers-color-scheme: light)');
    const applyTheme = (isLightPreferred) => {
      document.documentElement.classList.toggle('dark', isLightPreferred);
      setIsDarkMode(!isLightPreferred);
    };
    applyTheme(prefersLightScheme.matches);
    const handleChange = (e) => applyTheme(e.matches);
    prefersLightScheme.addEventListener('change', handleChange);
    return () => prefersLightScheme.removeEventListener('change', handleChange);
  }, []);
  
  const handleFilmstripClick = (artwork, index) => {
    setActiveIndex(index);
  };

  const handleFilmstripHover = (artwork) => {
    if (!shouldReduceMotion) {
      setHoveredArtwork(artwork);
    }
  };

  const openLightbox = () => setLightboxOpen(true);
  const closeLightbox = () => setLightboxOpen(false);

  const navigateLightbox = (direction) => {
    let newIndex = (activeIndex + direction + artworks.length) % artworks.length;
    setActiveIndex(newIndex);
  };

  const focusDisplayArtwork = hoveredArtwork || currentArtwork;

  return (
    <>
      <script dangerouslySetInnerHTML={{ __html: `/* Theme script from previous examples */` }} />

      <motion.div 
        className="bnw-page-container pg-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <header className="bnw-main-header">
          <motion.h1 
            className="pg-title-main-text"
            initial={{opacity:0, y:-20}} animate={{opacity:1, y:0, transition:{delay:0.1, duration:0.4}}}
          >
            Lines of <span className="pg-title-accent-text bnw-title-accent">Emotion</span>
          </motion.h1>
          <motion.p 
            className="pg-subtitle-text"
            initial={{opacity:0, y:-20}} animate={{opacity:1, y:0, transition:{delay:0.2, duration:0.4}}}
          >
            Exploring the expressive potential of line and shadow.
          </motion.p>
        </header>

        <div className="bnw-gallery-layout">
          <motion.div className="bnw-filmstrip-panel" ref={filmstripRef} initial="initial" animate="animate">
            {artworks.map((art, index) => (
              <FilmstripItem
                key={art.id}
                artwork={art}
                index={index}
                isActive={index === activeIndex}
                onClick={handleFilmstripClick}
                onHover={handleFilmstripHover}
              />
            ))}
          </motion.div>

          <motion.div className="bnw-focus-panel">
            <AnimatePresence mode="wait">
              <motion.div
                key={focusDisplayArtwork.id}
                className="bnw-focus-image-wrapper"
                onClick={openLightbox}
                initial={{ opacity: 0, scale: shouldReduceMotion ? 1 : 0.95 }}
                animate={{ opacity: 1, scale: 1, transition: { duration: 0.5, ease: [0.42, 0, 0.58, 1] } }}
                exit={{ opacity: 0, scale: shouldReduceMotion ? 1 : 0.9, transition: { duration: 0.3 } }}
                whileHover={shouldReduceMotion ? {} : { scale: 1.02, transition: { type: 'spring', stiffness: 200, damping:15} }}
              >
                <motion.img
                  src={focusDisplayArtwork.src}
                  alt={focusDisplayArtwork.alt}
                  className="bnw-focus-image"
                  layoutId={`bnw-main-image-${focusDisplayArtwork.id}`}
                />
                <div className="bnw-focus-image-overlay">
                    <Maximize size={32} />
                </div>
              </motion.div>
            </AnimatePresence>
            
            <motion.div className="bnw-focus-details">
              <AnimatePresence mode="wait">
                <motion.h2
                  key={`title-${currentArtwork.id}`}
                  className="bnw-focus-title text-color-heading"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0, transition: { delay: 0.2, duration: 0.4 } }}
                  exit={{ opacity: 0, y: -20, transition: { duration: 0.2 } }}
                >
                  {currentArtwork.title}
                </motion.h2>
              </AnimatePresence>
              <AnimatePresence mode="wait">
                <motion.p
                  key={`medium-${currentArtwork.id}`}
                  className="bnw-focus-medium text-color-paragraph"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0, transition: { delay: 0.25, duration: 0.4 } }}
                  exit={{ opacity: 0, y: -20, transition: { duration: 0.2 } }}
                >
                  {currentArtwork.medium} • {currentArtwork.dimensions} inches • {currentArtwork.year}
                </motion.p>
              </AnimatePresence>
                <motion.button 
                  className="bnw-view-details-button button-primary focus-ring-accent"
                  onClick={openLightbox}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1, transition: { delay: 0.35 } }}
                  whileHover={shouldReduceMotion ? {} : { scale: 1.05 }} whileTap={shouldReduceMotion ? {} : { scale: 0.95 }}
                >
                  View Full Details <ArrowRight size={18} className="ml-2"/>
                </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      <AnimatePresence>
        {lightboxOpen && currentArtwork && (
          <motion.div
            className="bnw-lightbox-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition:{delay: 0.2} }}
            onClick={closeLightbox}
          >
            <motion.div
              className="bnw-lightbox-content bg-card"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 50, scale: shouldReduceMotion ? 1 : 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1, transition: { type:'spring', stiffness:150, damping:20, delay:0.05 } }}
              exit={{ opacity: 0, y: shouldReduceMotion ? 0 : 30, scale: shouldReduceMotion ? 1 : 0.95, transition: { duration: 0.2 } }}
            >
              <motion.div className="bnw-lightbox-image-container">
                <AnimatePresence mode="sync">
                    <motion.img
                        key={currentArtwork.id}
                        layoutId={`bnw-main-image-${currentArtwork.id}`}
                        src={currentArtwork.src}
                        alt={currentArtwork.alt}
                        className="bnw-lightbox-image"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1, transition: { duration: 0.3, delay: 0.1 } }}
                        exit={{ opacity: 0, transition: {duration: 0.1} }}
                    />
                </AnimatePresence>
              </motion.div>
              <div className="bnw-lightbox-details-panel text-color-paragraph">
                 <motion.h2 className="bnw-lightbox-title text-color-heading"
                    initial={{opacity:0, y:10}} animate={{opacity:1, y:0, transition:{delay:0.2}}}
                 >{currentArtwork.title}</motion.h2>
                 <motion.p className="bnw-lightbox-meta"
                    initial={{opacity:0, y:10}} animate={{opacity:1, y:0, transition:{delay:0.25}}}
                 >
                    {currentArtwork.medium} <br/>
                    {currentArtwork.dimensions} inches • {currentArtwork.year}
                 </motion.p>
                 <motion.p className="bnw-lightbox-description"
                    initial={{opacity:0, y:10}} animate={{opacity:1, y:0, transition:{delay:0.3}}}
                 >
                    {currentArtwork.alt}
                 </motion.p>
                 <motion.button
                  className="mt-auto button-primary w-full py-3 text-base font-semibold focus-ring-accent"
                  initial={{opacity:0, y:10}} animate={{opacity:1, y:0, transition:{delay:0.35}}}
                  whileHover={shouldReduceMotion ? {} : { scale: 1.05 }} whileTap={shouldReduceMotion ? {} : { scale: 0.95 }}
                >
                  Enquire
                  <ExternalLink size={18} className="ml-2" />
                </motion.button>
              </div>
              <button onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }} className="bnw-lightbox-nav prev">
                <ChevronLeft size={30} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }} className="bnw-lightbox-nav next">
                <ChevronRight size={30} />
              </button>
            </motion.div>
            <motion.button onClick={closeLightbox} className="bnw-lightbox-close-btn" aria-label="Close lightbox"
                initial={{opacity:0, scale:0.7}} animate={{opacity:1, scale:1, transition:{delay:0.3}}} exit={{opacity:0, scale:0.7}}>
              <CloseIcon size={24} />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Drawings;